import classNames from "classnames";
import { FC, PropsWithChildren, useEffect, useRef, useState } from "react";
import { useModalLayer } from "../../context/modalContext";
import ModalRoot from "../ModalRoot";
import SvgClose from "../icons/Close";
import Button from "./Button";
import Icon from "./Icon";
import LoadingSpinner from "./LoadingSpinner";
import styles from "./ModalDialog.module.css";

type PropsBase = PropsWithChildren<{
  onClose: () => void;
  preTitleButton?: React.ReactNode;
  postTitleButton?: React.ReactNode;
  title?: string;
  className?: string;
  isOnlyTextDialog?: boolean;
  full?: boolean;
}>;

type PropsWithActions = PropsBase & {
  actions: ActionButton[];
  isSubmitting: boolean; // required if actions are present
};

type PropsWithoutActions = PropsBase & {
  actions?: undefined;
  isSubmitting?: undefined;
};

type ModalDialogProps = PropsWithActions | PropsWithoutActions;

export interface ActionButton {
  title: string;
  disabled?: boolean;
  onClick: () => void;
  type?: "primary" | "secondary";
  alignment?: "left" | "right";
}

const ModalDialog: FC<ModalDialogProps> = ({
  children,
  onClose,
  preTitleButton,
  title,
  postTitleButton,
  actions,
  isSubmitting,
  className,
  isOnlyTextDialog,
  full,
}: ModalDialogProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const { zIndex } = useModalLayer(onClose);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  // Drag logic
  useEffect(() => {
    const content = contentRef.current;
    const scrollArea = scrollAreaRef.current;
    const dragHandle = dragHandleRef.current;
    const header = headerRef.current;
    if (!content || !scrollArea || !dragHandle || !header) return;

    const scrollAreaStyle = getComputedStyle(scrollArea);
    const paddingTop = parseFloat(scrollAreaStyle.paddingTop);
    const headerHeight = header.getBoundingClientRect().height;

    const dragZoneHeight = paddingTop + headerHeight;
    dragHandle.style.height = `${dragZoneHeight}px`;

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;

      const rect = content.getBoundingClientRect();
      // content.style.width = `${rect.width}px`;
      // content.style.height = `${rect.height}px`;

      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });

      setDragging(true);

      content.style.position = "fixed";
      content.style.margin = "0";
      content.style.left = `${rect.left}px`;
      content.style.top = `${rect.top}px`;
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!dragging) return;

      const modalWidth = content.offsetWidth;
      const modalHeight = content.offsetHeight;

      const maxLeft = window.innerWidth - modalWidth;
      const maxTop = window.innerHeight - modalHeight;

      let left = e.clientX - dragOffset.x;
      let top = e.clientY - dragOffset.y;

      left = Math.max(0, Math.min(left, maxLeft));
      top = Math.max(0, Math.min(top, maxTop));

      content.style.left = `${left}px`;
      content.style.top = `${top}px`;
    };

    const onMouseUp = () => {
      if (!dragging) return;
      setDragging(false);
    };

    dragHandle.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      dragHandle.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [dragging, dragOffset]);

  return (
    <ModalRoot>
      <div
        className={classNames(styles.dialog, {
          [styles.visible]: visible,
        })}
        // onClick={(e) => e.stopPropagation()}
        style={{ zIndex: (zIndex ?? 0) + 1 }}
      >
        <div
          ref={contentRef}
          className={classNames(styles.content, className, {
            [styles.isOnlyText]: isOnlyTextDialog,
            [styles.full]: full,
          })}
        >
          <div ref={dragHandleRef} className={styles.dragZone} />
          <div ref={scrollAreaRef} className={styles.scrollArea}>
            <div ref={headerRef} className={classNames(styles.header, { [styles["float-right"]]: !title })}>
              {preTitleButton}
              {title && <div className={styles.title}>{title}</div>}
              {postTitleButton}
              <button className={styles["close-button"]} onClick={onClose}>
                <Icon className={styles.icon} glyph={SvgClose} />
              </button>
            </div>

            {children}

            {actions && (
              <div className={styles.buttons}>
                <div className={styles.leftButtons}>
                  {actions
                    ?.filter((a) => a.alignment === "left")
                    .map(({ onClick, title, disabled, type }, index) => (
                      <Button
                        key={index}
                        buttonProps={{
                          disabled,
                          onClick,
                        }}
                        type={type}
                      >
                        {title}
                      </Button>
                    ))}
                </div>
                {!!isSubmitting && <LoadingSpinner />}
                <div className={styles.rightButtons}>
                  {actions
                    ?.filter((a) => a.alignment !== "left")
                    .map(({ onClick, title, disabled, type }, index) => (
                      <Button
                        key={index}
                        buttonProps={{
                          disabled,
                          onClick,
                        }}
                        type={type}
                      >
                        {title}
                      </Button>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalRoot>
  );
};

export default ModalDialog;
